import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Container,
  createStyles,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  withStyles,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router-dom';
import { OrderHistoryItem } from '../hooks/useOrderHistory';
import { useToken } from '../hooks/useToken';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../data-management/store';
import { AuthState } from '../data-management/authSlice';
import { FieldType } from '../data-management/productsSlice';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { isInomatCategory } from '../data-management/inomatHelper';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(5),
    '@media print': {
      padding: 0,
      backgroundColor: 'white',
    },
  },
  rightSidebar: {
    padding: theme.spacing(3),
    boxShadow: '0px 2px 4px 2px rgba(83, 83, 83, 0.16)',
    '@media print': {
      position: 'absolute',
      width: 'auto',
      boxSizing: 'border-box',
      padding: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: '100%',
      height: 'auto',
      boxShadow: 'unset',
    },
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    '& div.item': {
      width: '8.3%',
      textAlign: 'left',
    },
  },
  infoRow: {
    color: '#535353',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    '@media print': {
      backgroundColor: '#F8F8F8',
    },
    padding: '5px 10px',
    borderBottom: '1px solid rgba(196,196,196,0.5)',
  },
  tableCellBreakWord: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: 100,
  },
  tableCellExtra: {
    paddingBottom: 4,
    paddingTop: 0,
    border: 'none',
    '@media print': {
      lineHeight: '1',
      fontSize: 10,
    },
  },
  subTitle: {
    fontSize: 16,
    textDecoration: 'underline',
    opacity: 0.8,
    color: '#535353',
    marginBottom: 16,
  },
  amount: {
    textAlign: 'right',
  },
  tableRow: {
    borderTop: '1pt solid rgba(0, 0, 0, 0.12)',
  },
  removeDividiers: {
    '@media print': {
      display: 'none',
    },
  },
  header: {
    paddingLeft: '250px'
  },
  styledTableRow: {
    borderTop: '1pt solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#F8F8F8',
    '@media print': {
      backgroundColor: '#F8F8F8',
    },
  },
  styledTableRowExtra: {
    backgroundColor: '#F8F8F8',
    '@media print': {
      backgroundColor: '#F8F8F8',
    },
  },
}));

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    root: {
      border: 'none',
      '@media print': {
        lineHeight: '1',
        fontSize: 10,
      },
    },
  }),
)(TableCell);

const StyledHeadTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#F8F8F8',
    },
  }),
)(TableRow);

type OrderHistoryItemExtended = OrderHistoryItem & {
  org_name: {
    name: string;
  };
  user_mail: {
    name: string;
  };
  shipping_address: {
    name: string;
  };
  totalArea: {
    name: string;
  };
  totalBrutPrice: {
    name: string;
  };
  totalNetPrice: {
    name: string;
  };
  stored: any;
  product_names: any;
  billingAddress: {
    address: string;
    city: string;
    id: number;
    zip: string;
  };
  statuses: {
    code: number;
    description: string;
    name: string;
    created_at: string;
  }[];
};

const SvgLogo = () => {
  return (
    <svg
      width="159"
      height="70"
      viewBox="0 0 159 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M71.3883 33.618C69.8903 33.618 68.2597 33.618 66.7616 33.618C66.7311 23.9589 66.7616 14.3101 66.7514 4.65103C46.0536 4.65103 25.3456 4.65103 4.64782 4.65103C4.63763 24.8871 4.64782 45.1231 4.64782 65.3592C25.3558 65.3592 46.0536 65.3592 66.7616 65.3592C66.7616 63.8802 66.7616 62.4013 66.7616 60.9223C68.3107 60.9223 69.8597 60.9121 71.3985 60.9223C71.3985 63.4518 71.3985 65.9814 71.3985 68.5109C71.3985 69.0004 71.4189 69.5002 71.368 69.9898C47.5925 70 23.8068 69.9898 0.0313197 70C-0.029826 68.4497 0.0211287 66.8993 0.000746771 65.3592C0.000746771 50.4371 0.000746771 35.5049 0.000746771 20.5828C0.0109377 13.7185 -0.00944414 6.86435 0.0109378 0C23.7966 0.0101996 47.5925 -0.0101998 71.3781 0.0101995C71.3985 9.65904 71.3781 19.2977 71.3883 28.9465"
        fill="#221E1F"
      />
      <path
        d="M116.289 37.7998C117.41 37.6876 118.562 37.657 119.663 37.861C120.06 38.1058 120.559 38.0548 120.947 38.32C121.191 38.4934 121.497 38.5546 121.731 38.7484C121.925 38.9014 122.149 39.0238 122.363 39.1462C122.628 39.4521 122.975 39.6867 123.24 39.9927C123.647 40.5537 124.147 41.0841 124.32 41.7777C124.717 42.3488 124.595 43.0934 124.87 43.7156C125.013 44.8987 124.942 46.1023 124.962 47.2956C124.952 48.4482 125.013 49.6008 124.86 50.7431C124.626 51.2837 124.677 51.9059 124.452 52.4465C124.289 52.6811 124.218 52.9666 124.116 53.2216C123.81 53.6908 123.484 54.1498 123.138 54.5986C122.486 55.1188 121.884 55.7205 121.12 56.0571C120.773 56.1693 120.427 56.2917 120.101 56.4549C118.939 56.7303 117.737 56.7099 116.554 56.6283C116.126 56.5671 115.668 56.5467 115.281 56.3325C114.903 56.1591 114.465 56.1081 114.139 55.8531C113.395 55.3941 112.733 54.8128 112.132 54.1702C111.928 53.9152 111.856 53.5786 111.612 53.3542C111.428 53.0788 111.398 52.7218 111.204 52.4465C111.031 51.9875 111 51.4979 110.909 51.0185C110.684 50.5595 110.756 50.0292 110.715 49.5396C110.695 48.489 110.715 47.4384 110.705 46.3777C110.705 45.4903 110.684 44.6029 110.797 43.7156C111.072 43.0934 110.949 42.3488 111.347 41.7777C111.449 41.2677 111.816 40.8801 112.04 40.4211C112.437 39.9519 112.896 39.5133 113.354 39.1054C113.568 38.983 113.782 38.8504 113.976 38.6974C114.2 38.5342 114.475 38.473 114.7 38.3098C115.067 38.0548 115.535 38.1058 115.912 37.8814C116.035 37.8304 116.167 37.8202 116.289 37.7998ZM117.105 41.9612C116.789 42.0428 116.432 42.1142 116.249 42.41C115.882 42.6854 115.739 43.1444 115.546 43.5422C115.321 44.7865 115.382 46.0513 115.332 47.3058C115.393 48.5196 115.301 49.7538 115.566 50.9471C115.76 51.2327 115.79 51.6407 116.106 51.8447C116.422 52.2323 116.87 52.5485 117.39 52.5893C118.114 52.7014 118.929 52.5688 119.428 52.0079C119.551 51.8243 119.795 51.7325 119.846 51.4979C119.938 51.2225 120.142 50.9879 120.172 50.6921C120.345 49.5396 120.305 48.3666 120.345 47.2039C120.305 46.0309 120.345 44.8579 120.162 43.6952C120.131 43.4096 119.928 43.1852 119.836 42.92C119.724 42.614 119.418 42.4508 119.235 42.1958C118.572 41.8796 117.808 41.8083 117.105 41.9612Z"
        fill="#221E1F"
      />
      <path
        d="M13.8298 38.0242C15.175 38.0344 16.5202 38.014 17.8552 38.0344C19.017 39.9009 20.2093 41.7471 21.422 43.583C21.8501 44.3888 22.4717 45.0823 22.8691 45.9085C23.1749 46.3165 23.4296 46.7653 23.7456 47.1631C23.766 44.1338 23.7558 41.0943 23.7456 38.065C25.2946 37.9834 26.8436 38.0548 28.3926 38.0242C28.3926 44.2154 28.3926 50.3963 28.3926 56.5875C27.0169 56.5773 25.6309 56.6181 24.2551 56.5671C23.8577 55.5982 23.0933 54.8332 22.6348 53.8846C22.3494 53.4562 22.0233 53.0584 21.7787 52.5995C21.4934 52.2833 21.3915 51.8549 21.0959 51.5387C20.6169 50.7023 20.0055 49.968 19.5571 49.1112C19.2819 48.8154 19.1494 48.438 18.8947 48.132C18.752 47.826 18.4768 47.6118 18.3546 47.2957C18.3647 50.3861 18.3546 53.4868 18.3546 56.5773C16.8463 56.5773 15.3278 56.5773 13.8196 56.5773C13.8298 50.3963 13.8298 44.2154 13.8298 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M32.0717 38.0242C36.3009 38.0242 40.5301 38.0242 44.7594 38.0242C44.7594 39.3807 44.7594 40.7271 44.7594 42.0836C42.0792 42.0836 39.3989 42.0836 36.7187 42.0836C36.7187 43.1138 36.7187 44.1338 36.7187 45.1639C39.0423 45.1741 41.3556 45.1435 43.6791 45.1843C43.6689 46.5307 43.6689 47.8668 43.6791 49.2132C41.3556 49.2438 39.0423 49.203 36.7187 49.2336C36.7187 50.3351 36.7187 51.4367 36.7187 52.5383C39.3989 52.5587 42.0792 52.5383 44.7696 52.5485C44.7797 53.905 44.7696 55.2514 44.7696 56.6079C40.5403 56.6079 36.3111 56.6079 32.0818 56.6079C32.0717 50.3963 32.0717 44.2154 32.0717 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M46.9504 38.0242C51.516 38.0242 56.0917 38.0242 60.6573 38.0242C60.6573 39.3808 60.6573 40.7271 60.6573 42.0837C59.149 42.0837 57.6407 42.0837 56.1223 42.0837C56.1223 46.9183 56.1223 51.7529 56.1223 56.5875C54.5733 56.5671 53.0242 56.6385 51.4752 56.5467C51.4956 51.7223 51.4752 46.8979 51.4854 42.0735C49.9771 42.0735 48.4689 42.0735 46.9504 42.0735C46.9504 40.7271 46.9504 39.3808 46.9504 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M62.2369 38.0242C66.8025 38.0242 71.3782 38.0242 75.9438 38.0242C75.9438 39.3807 75.9438 40.7271 75.9438 42.0836C74.4355 42.0836 72.9272 42.0836 71.4088 42.0836C71.4088 46.9183 71.4088 51.7529 71.4088 56.5875C69.8598 56.5875 68.3107 56.5875 66.7719 56.5875C66.7719 51.7529 66.7719 46.9183 66.7719 42.0836C65.2636 42.0836 63.7554 42.0836 62.2369 42.0836C62.2369 40.7271 62.2369 39.3807 62.2369 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M78.6444 38.0242C82.8737 38.0242 87.1029 38.0242 91.3322 38.0242C91.3322 39.3807 91.3322 40.7271 91.3322 42.0836C88.6519 42.0836 85.9717 42.0836 83.2813 42.0836C83.2813 43.1444 83.2813 44.2052 83.2813 45.2659C85.6048 45.2761 87.9182 45.2455 90.2417 45.2863C90.2213 46.6633 90.2519 48.0402 90.2315 49.4172C87.9182 49.4376 85.6048 49.4172 83.2813 49.4274C83.2813 51.8141 83.2813 54.2008 83.2813 56.5875C81.7323 56.5977 80.1934 56.5875 78.6444 56.5875C78.6444 50.3963 78.6444 44.2154 78.6444 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M94.2365 38.0242C96.3766 38.0242 98.5167 38.0242 100.657 38.0242C101.706 38.0344 102.776 37.963 103.785 38.269C104.071 38.4526 104.407 38.5138 104.723 38.6362C104.866 38.7381 104.988 38.8605 105.151 38.9217C105.691 39.1563 106.109 39.5949 106.517 40.0029C106.802 40.4619 107.169 40.8801 107.413 41.3696C107.515 41.6654 107.576 41.9816 107.77 42.2366C107.994 43.2668 108.015 44.3479 107.831 45.3781C107.475 46.5409 106.771 47.622 105.773 48.3258C105.538 48.5502 105.243 48.6828 104.947 48.7848C104.937 49.0806 105.212 49.2438 105.294 49.4987C105.447 49.9271 105.742 50.2841 105.905 50.7125C106.15 50.9777 106.18 51.3653 106.415 51.6407C106.629 51.8957 106.649 52.2526 106.843 52.528C107.077 52.8034 107.108 53.191 107.352 53.4562C107.505 53.7112 107.576 54.007 107.77 54.2518C107.943 54.619 108.086 55.0065 108.331 55.3329C108.463 55.7817 108.83 56.1183 108.922 56.5875C107.148 56.5875 105.375 56.5875 103.602 56.5875C103.49 56.1591 103.154 55.8327 103.062 55.3941C102.827 54.9861 102.654 54.5374 102.41 54.1294C102.247 53.7724 102.165 53.3848 101.92 53.0788C101.666 52.4668 101.38 51.8753 101.054 51.3041C100.901 50.9777 100.861 50.5901 100.606 50.3147C100.473 50.1107 100.422 49.8761 100.341 49.6517C99.8211 49.6517 99.3014 49.6517 98.7816 49.6517C98.7816 51.9671 98.7816 54.2722 98.7816 56.5875C97.2734 56.5977 95.7549 56.5875 94.2466 56.5875C94.2364 50.3963 94.2365 44.2153 94.2365 38.0242ZM98.7714 42.104C98.7714 43.3586 98.7714 44.6131 98.7714 45.8779C99.709 45.8779 100.647 45.8779 101.584 45.8779C101.89 45.8881 102.124 45.6739 102.399 45.5821C102.675 45.5005 102.776 45.2149 103.011 45.0721C103.133 44.8171 103.235 44.5417 103.357 44.2969C103.51 43.8176 103.215 43.3994 103.041 42.9812C102.787 42.6242 102.42 42.3692 102.022 42.1856C100.952 42.0224 99.8619 42.1346 98.7714 42.104Z"
        fill="#221E1F"
      />
      <path
        d="M128.07 38.0242C129.416 38.0344 130.751 38.014 132.096 38.0344C132.483 38.728 133.003 39.3399 133.39 40.0437C133.523 40.3905 133.798 40.6557 133.981 40.9821C134.491 41.7062 134.919 42.4814 135.408 43.2158C135.683 43.5932 135.979 43.9706 136.152 44.3989C136.784 45.2965 137.365 46.2349 137.976 47.1528C138.017 44.1236 137.996 41.0841 137.986 38.0548C139.535 37.9732 141.084 38.0446 142.633 38.014C142.633 44.2051 142.633 50.3861 142.633 56.5773C141.319 56.5773 139.994 56.5875 138.679 56.5671C138.353 56.3121 138.2 55.8837 137.935 55.5675C137.609 54.9045 137.14 54.3232 136.753 53.6806C136.223 53.0278 135.897 52.2425 135.388 51.5795C134.776 50.6309 134.205 49.6619 133.543 48.744C133.36 48.3156 133.064 47.9484 132.819 47.5608C132.768 47.5404 132.656 47.4996 132.605 47.4792C132.605 50.5085 132.605 53.5276 132.605 56.5569C131.097 56.5671 129.579 56.5569 128.07 56.5569C128.07 50.3963 128.07 44.2153 128.07 38.0242Z"
        fill="#221E1F"
      />
      <path
        d="M145.293 38.0242C149.859 38.0242 154.434 38.0242 159 38.0242C159 39.3808 159 40.7271 159 42.0837C157.492 42.0837 155.983 42.0837 154.465 42.0837C154.465 46.9183 154.465 51.7529 154.465 56.5875C152.916 56.5671 151.367 56.6385 149.818 56.5467C149.838 51.7223 149.828 46.8979 149.828 42.0735C148.32 42.0735 146.801 42.0735 145.293 42.0735C145.293 40.7271 145.293 39.3808 145.293 38.0242Z"
        fill="#221E1F"
      />
    </svg>
  );
};

const OrderDetails: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [data, setData] = useState<OrderHistoryItemExtended | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | undefined>(t('saved_order_print_view_loading_state'))
  const [fields, setFields] = useState<FieldType[]>([]);

  const {
    params: { id },
  } = useRouteMatch<{
    id: string | undefined;
  }>();

  const token = useToken();

  useEffect(() => {
    axios
      .get<OrderHistoryItemExtended>(
        `${process.env.REACT_APP_SERVER_URL}/order/saved/one/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(({ data }) => {
        setData(data);

        axios
        .get<{
          id: string | number
          fields: FieldType[]
        }[]>(`${process.env.REACT_APP_SERVER_URL}/categories`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          setFields(res.data.find(({ id }) => id === data.category_id)?.fields ?? [])
          window.print();
        });
      }).catch(e => {
        setError(t('saved_order_print_view_order_does_not_exists'));
      })

      
  }, [id]);

  let fieldNumber = 0;
  if (data && data.items && data.items[0]) {
    fieldNumber = data?.items[0]?.features?.findIndex(({ code }) => code === 'MAG') ?? 0;
  }
  if (fieldNumber === -1) fieldNumber = 0;

  type ExtraProps = {
    name: string;
    value: string;
  };

  const user = useSelector<RootState, AuthState['user']>(
    (state) => state.auth.user,
  );

  function getParameterByName(name: string, url = window.location.href) {
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  var price_option = getParameterByName('price_option');
  var percentage = getParameterByName('percentage');

  let total_net = 0;
  let total_brut = 0;
  let netto = 0;
  let brutto = 0;
  if (data) {
    switch (price_option) {
      case 'my_price':
        total_net = data.totalNetPrice;
        total_brut = data?.totalBrutPrice;
        break;
      case 'list_price':
        data.items.filter((prod_piece: any) => { return !prod_piece.validationState || prod_piece.validationState != "incomplete" }).map((item, itemIndex) => {
          total_net += data.stored[itemIndex].RunningTotal ? data.stored[itemIndex].RunningTotal * item.quantity : 0;
          total_brut += data.stored[itemIndex].RunningTotal ? (data.stored[itemIndex].RunningTotal * 1.27 * item.quantity) : 0;
        });
        break;
      case 'price_gap':
        total_net = data.totalNetPrice * ((((Number(percentage))) / 100) + 1);
        total_brut = data?.totalBrutPrice * ((((Number(percentage))) / 100) + 1);
        break;
    }
  }

  let tableHeader = [
    
    ...((data?.items?.filter((prod_piece: any) => { return !prod_piece.validationState || prod_piece.validationState != "incomplete" })[0]?.features
      .filter(({ code }) => { return code == "PROD" || code == "VA" || code == "ELZ" || code == "FENY" || code == "PANT" || code == "SZIN" })
      .map(({ code }) => t(fields.find((field) => field.code == code)?.label ?? "") ?? "")) ?? []),
    t('saved_order_print_view_table_height'),
    t('saved_order_print_view_table_width'),
    t('saved_order_print_view_table_count'),
    t('saved_order_print_view_table_size'),
    t('saved_order_print_view_table_net'),
    t('saved_order_print_view_table_gross')
  ];

  if (!isInomatCategory(data?.category_id)) {
    tableHeader.unshift(t('saved_order_print_view_table_product'));
  }

  return (
    <div className={classes.root} id="order-history">
      {data ? (
        <Grid container spacing={5}>
          <Grid item lg={9}>
            <Paper className={classes.rightSidebar}>
              <div className={classes.flexRow} style={{ marginBottom: 50 }}>
                <SvgLogo />
                <div className={classes.header}>
                  <Typography variant="h3">
                    {t('saved_order_print_view_title_text')}
                  </Typography>
                  <Typography variant="body1">
                    {format(
                      parseISO(data.created_at),
                      'yyyy.MM.dd.',
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {t('saved_order_print_view_expiry_info')}
                  </Typography>
                </div>
              </div>
              <div
                style={{
                  marginBottom: 20,
                }}
              >
                <Typography variant="h4" style={{ fontWeight: 300 }}>
                  {t('saved_order_print_view_creator_data')}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 300 }}>
                  {data.org_name}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 300 }}>
                  {data.user_mail}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 300 }}>
                  {data.shipping_address}
                </Typography>
              </div>
              <Container className={classes.removeDividiers}>
                <Box mt={5} />
                <Divider />
                <Box mt={5} />
              </Container>
              <Box mt={5} />
              <Typography
                variant="body1"
                style={{
                  textDecoration: 'underline',
                  marginBottom: 20,
                  opacity: 0.8,
                }}
              >
                {t('saved_order_print_view_products_data')}
              </Typography>
              <Table>
                <TableHead>
                  <StyledHeadTableRow>
                    {tableHeader.map((name, index) =>
                      <StyledTableCell key={index}>{name}</StyledTableCell>
                    )}
                  </StyledHeadTableRow>
                </TableHead>
                <TableBody>
                {data.items.filter((prod_piece: any) => { return !prod_piece.validationState || prod_piece.validationState != "incomplete" }).map((item, itemIndex) => {
                  const height = parseInt(
                    item.features.find(({ code }) => code === 'MAG')
                      ?.keyed_value ?? '0',
                  );
                  const width = parseInt(
                    item.features.find(({ code }) => code === 'SZEL')
                      ?.keyed_value ?? '0',
                  );
                  switch (price_option) {
                    case 'my_price':
                      netto = data.stored[itemIndex].Price
                      brutto = data.stored[itemIndex].PriceBrutto
                      break;
                    case 'list_price':
                      netto = data.stored[itemIndex].RunningTotal ? data.stored[itemIndex].RunningTotal : 0
                      brutto = data.stored[itemIndex].RunningTotal ? data.stored[itemIndex].RunningTotal * 1.27 : 0
                      break;
                    case 'price_gap':
                      netto = data.stored[itemIndex].Price * ((((Number(percentage))) / 100) + 1)
                      brutto = data.stored[itemIndex].PriceBrutto * ((((Number(percentage))) / 100) + 1)
                      break;
                  }
                  return (
                    <>
                    <TableRow
                      key={item.id}
                      className={itemIndex % 2 == 1 ? classes.styledTableRow : ""}
                    >
                      {!isInomatCategory(data.category_id) && <StyledTableCell>{data.product_names[itemIndex]}</StyledTableCell>}
                      {item.features
                        .filter(({ code }) => { return code == "PROD" || code == "ELZ" || code == "FENY" || code == "PANT" || code == "VA" || code == "SZIN" })
                        .map(({ option_code, keyed_value, option_description }, index) => (
                          <StyledTableCell key={index}>
                            {option_description ?? option_code?.replace('_INOMAT', '')} 
                            {option_code == "RAL_NCS" ? " (" + (keyed_value ?? "") + ")" : ""}
                          </StyledTableCell>
                        ))}
                      <StyledTableCell>{height} mm</StyledTableCell>
                      <StyledTableCell>{width} mm</StyledTableCell>
                      <StyledTableCell>{item.quantity}</StyledTableCell>
                      <StyledTableCell>
                        {data.stored[itemIndex].Meret.toFixed(4)} m2
                      </StyledTableCell>
                      <StyledTableCell>
                        {netto ? netto?.toLocaleString('hu-HU', {
                          style: 'currency',
                          currency: 'HUF',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }) : t('saved_order_print_view_no_price')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {brutto ? brutto?.toLocaleString('hu-HU', {
                          style: 'currency',
                          currency: 'HUF',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }) : t('saved_order_print_view_no_price')}
                      </StyledTableCell>
                    </TableRow>
                    {data.stored[itemIndex].Extrak?.length ?
                    <TableRow className={itemIndex % 2 == 1 ? classes.styledTableRowExtra : ""}>
                      <TableCell className={classes.tableCellExtra} colSpan={7}>
                        {data.stored[itemIndex].Extrak.map(
                            ({ name, value }: ExtraProps) => `${name}: ${value}`,
                        ).join(', ') }
                      </TableCell>
                    </TableRow>
                    : <></>}
                    {data.stored[itemIndex].Note?.length ?
                    <TableRow className={itemIndex % 2 == 1 ? classes.styledTableRowExtra : ""}>
                      <TableCell className={classes.tableCellExtra} colSpan={7}>
                      {t('saved_order_print_view_comment_text')} {data.stored[itemIndex].Note}
                      </TableCell>
                    </TableRow>
                    : <></>}
                    </>
                  );
                })}
                </TableBody>
              </Table>
              <Box mt={5} />
              <Divider />
              <Box mt={5} />
              <Grid container spacing={6}>
                <Grid
                  item
                  xs={6}
                  container
                  direction={'column'}
                >
                  <Typography className={classes.subTitle}>
                    {t('saved_order_print_view_summary_text')}
                  </Typography>
                  <div className={classes.infoRow}>
                    <Typography variant="body1">{t('saved_order_print_view_product_quantity_text')}</Typography>
                    <Typography variant="body1">
                      {data.items.filter((prod_piece: any) => { return !prod_piece.validationState || prod_piece.validationState != "incomplete" }).reduce((acc, item) => acc + item.quantity, 0)}{' '}
                      {t('saved_order_print_view_count_text')}
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography variant="body1">
                      {t('saved_order_print_view_product_size_of_all_text')}
                    </Typography>
                    <Typography variant="body1">
                      {data.totalArea?.toFixed(4)} m2
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography variant="body1">{t('saved_order_print_view_net_text')}</Typography>
                    <Typography variant="body1">
                      {total_net?.toLocaleString('hu-HU', {
                        style: 'currency',
                        currency: 'HUF',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography variant="body1">{t('saved_order_print_view_vat_text')}</Typography>
                    <Typography variant="body1">
                      {(
                        (total_brut ?? 0) - (total_net ?? 0)
                      )?.toLocaleString('hu-HU', {
                        style: 'currency',
                        currency: 'HUF',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </div>
                  <div className={classes.infoRow} style={{ border: 'unset' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                      {t('saved_order_print_view_price_summary_text')}
                    </Typography>
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                      {total_brut?.toLocaleString('hu-HU', {
                        style: 'currency',
                        currency: 'HUF',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <>
        <Box mb={3}><Alert style={{justifyContent: "center"}} severity="info">{error}</Alert></Box>
        </>
      )}
    </div>
  );
};

export default OrderDetails;
